import { useState, useEffect, useCallback } from 'react';
import {
  getSurveyList, createSurvey, updateSurvey, deleteSurvey,
  updateSurveyStatus, getSurveyItems, saveSurveyItems, getSurveyResult,
  downloadSurveyExcel, copySurvey
} from '../api/surveyApi';
import { QRCodeSVG } from 'qrcode.react';

const YEAR = new Date().getFullYear().toString();

const getYearOptions = () => {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = current + 1; y >= current - 3; y--) years.push(y.toString());
  return years;
};

const ITEM_TYPES = [
  { value: 'T1', label: '주관식 단답형' },
  { value: 'T2', label: '주관식 서술형' },
  { value: 'M',  label: '객관식' },
  { value: 'C',  label: '체크박스' },
  { value: 'S',  label: '구분 (중간 제목)' },
  { value: 'V',  label: '영상 (유튜브)' },
];
const STATUS_LABEL = { S: '대기', P: '진행중', E: '종료' };
const STATUS_COLOR = { S: '#aaa', P: '#52c41a', E: '#ff4d4f' };

const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

export default function SurveyPage() {
  const [view, setView]         = useState('list');   // list | form | items | result
  const [list, setList]         = useState([]);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [selected, setSelected] = useState(null);
  const [form, setForm]         = useState({ title: '', target_type: 'A' });
  const [items, setItems]       = useState([]);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [qrTarget, setQrTarget] = useState(null);

  const loadList = useCallback(async (year = yearFilter) => {
    const res = await getSurveyList(year);
    if (res.success) setList(res.data);
  }, [yearFilter]);

  useEffect(() => { loadList(yearFilter); }, [yearFilter]);

  // ==================== 핸들러 ====================

  const handleCreate = () => {
    setSelected(null);
    setForm({ title: '', target_type: 'A' });
    setError('');
    setView('form');
  };

  const handleEdit = (item) => {
    setSelected(item);
    setForm({ title: item.title, target_type: item.target_type || 'A' });
    setError('');
    setView('form');
  };

  const handleSaveForm = async () => {
    setError('');
    if (!form.title.trim()) { setError('제목을 입력해주세요.'); return; }
    setLoading(true);
    try {
      if (selected) {
        const res = await updateSurvey(selected.survey_year, selected.seq_no, { title: form.title, target_type: form.target_type });
        if (!res.success) { setError(res.message); return; }
      } else {
        const res = await createSurvey({ survey_year: YEAR, title: form.title, target_type: form.target_type });
        if (!res.success) { setError(res.message); return; }
      }
      await loadList();
      setView('list');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    const confirmMsg = item.status === 'E'
      ? '설문을 삭제하는 경우, 결과를 더이상 확인할 수 없습니다.\n그래도 삭제하시겠습니까?'
      : '삭제하시겠습니까?';
    if (!window.confirm(confirmMsg)) return;
    const res = await deleteSurvey(item.survey_year, item.seq_no);
    if (res.success) loadList();
  };

  const handleStatus = async (item, status) => {
    const res = await updateSurveyStatus(item.survey_year, item.seq_no, status);
    if (res.success) loadList();
  };

  const handleItems = async (item) => {
    setSelected(item);
    const res = await getSurveyItems(item.survey_year, item.seq_no);
    if (res.success) {
      setItems(res.data.map(it => ({
        ...it,
        options: it.options?.map(o => o.opt_name) || ['', ''],
        img_preview: it.img_data || null,
      })));
    } else {
      setItems([]);
    }
    setError('');
    setView('items');
  };

  const handleResult = async (item) => {
    setSelected(item);
    setLoading(true);
    try {
      const res = await getSurveyResult(item.survey_year, item.seq_no);
      if (res.success) setResult(res.data);
      else setError(res.message);
    } finally {
      setLoading(false);
    }
    setView('result');
  };

  const handleCopy = async (item) => {
    if (!window.confirm(`"${item.title}"을 복사하시겠습니까?`)) return;
    const res = await copySurvey(item.survey_year, item.seq_no);
    if (res.success) {
      await loadList();
      alert('✅ 복사되었습니다.');
    } else {
      alert(`❌ ${res.message}`);
    }
  };

  // ==================== 항목 편집 ====================

  const addItem = () => {
    setItems(prev => [...prev, { item_type: 'T1', item_title: '', options: ['', ''], img_data: null, img_preview: null, required_yn: 'N' }]);
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const updateItem = (idx, key, val) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [key]: val } : it));
  };

  const addOption = (idx) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, options: [...it.options, ''] } : it));
  };

  const removeOption = (idx, oi) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, options: it.options.filter((_, j) => j !== oi) } : it));
  };

  const updateOption = (idx, oi, val) => {
    setItems(prev => prev.map((it, i) => i === idx
      ? { ...it, options: it.options.map((o, j) => j === oi ? val : o) }
      : it));
  };

  const handleImgUpload = (idx, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { alert('이미지는 1MB 이하만 업로드 가능합니다.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      updateItem(idx, 'img_data', ev.target.result);
      updateItem(idx, 'img_preview', ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveItems = async () => {
    setError('');
    for (const it of items) {
      if (it.item_type === 'S') {
        if (!it.item_title.trim()) { setError('구분 항목의 제목을 입력해주세요.'); return; }
        continue;
      }
      if (it.item_type === 'V') {
        if (!it.item_title.trim()) { setError('영상 URL을 입력해주세요.'); return; }
        if (!getYoutubeEmbedUrl(it.item_title)) { setError('올바른 유튜브 URL 형식이 아닙니다.'); return; }
      }
      if (!it.item_title.trim()) { setError('질문 내용을 모두 입력해주세요.'); return; }
      if (['M', 'C'].includes(it.item_type) && it.options.some(o => !o.trim())) {
        setError('보기 내용을 모두 입력해주세요.'); return;
      }
    }
    setLoading(true);
    try {
      const res = await saveSurveyItems(selected.survey_year, selected.seq_no, items);
      if (!res.success) { setError(res.message); return; }
      await loadList();
      setView('list');
    } finally {
      setLoading(false);
    }
  };

  const handleExcelDownload = async () => {
    try {
      await downloadSurveyExcel(selected.survey_year, selected.seq_no);
    } catch (err) {
      alert('❌ 엑셀 다운로드에 실패했습니다.');
    }
  };

  // 링크 복사 기능
  const fallbackCopy = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
      alert('✅ 링크가 복사되었습니다!');
    } catch {
      alert('❌ 복사 실패. 직접 복사해주세요:\n' + text);
    }
    document.body.removeChild(textarea);
  };

  const buildUrl = (item) =>
    `${window.location.origin}/?page=survey&branch=${item.branch_code}&division=${item.division_code}&year=${item.survey_year}&seq=${item.seq_no}`;

  // ==================== 렌더 ====================

  if (view === 'form') return (
    <div style={S.wrap}>
      <div style={S.header}>
        <button style={S.backBtn} onClick={() => setView('list')}>← 목록</button>
        <h2 style={S.title}>{selected ? '설문 수정' : '설문 등록'}</h2>
      </div>
      <div style={S.card}>
        <div style={S.field}>
          <label style={S.label}>구분 *</label>
          <select style={S.input} value={form.target_type}
            onChange={e => setForm(p => ({ ...p, target_type: e.target.value }))}
            disabled={!!selected && selected.answer_cnt > 0}>
            <option value="A">전직원 (비로그인, 익명)</option>
            <option value="U">조합원 (로그인, 사번 포함)</option>
          </select>
          {selected && selected.answer_cnt > 0 && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              이미 제출된 응답이 있어 구분을 변경할 수 없습니다.
            </div>
          )}
        </div>
        <div style={S.field}>
          <label style={S.label}>제목 *</label>
          <input style={S.input} value={form.title}
            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            placeholder="설문 제목 입력" />
        </div>
        {error && <div style={S.error}>{error}</div>}
        <button style={{ ...S.btn, ...S.btnPrimary, opacity: loading ? 0.7 : 1 }}
          onClick={handleSaveForm} disabled={loading}>
          {loading ? '저장 중...' : '저장'}
        </button>
      </div>
    </div>
  );

  if (view === 'items') return (
    <div style={S.wrap}>
      <div style={S.header}>
        <button style={S.backBtn} onClick={() => setView('list')}>← 목록</button>
        <h2 style={S.title}>항목 편집 — {selected?.title}</h2>
      </div>

      {items.map((it, idx) => (
        <div key={idx} style={{ ...S.card, marginBottom: 16, borderLeft: it.item_type === 'S' ? '4px solid #722ed1' : '4px solid #1677ff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontWeight: 700, color: it.item_type === 'S' ? '#722ed1' : '#1677ff' }}>
              {it.item_type === 'S' ? `구분 ${idx + 1}` : `항목 ${idx + 1}`}
            </span>
            <button style={{ ...S.btn, ...S.btnDanger, padding: '4px 12px', fontSize: 12 }}
              onClick={() => removeItem(idx)}>삭제</button>
          </div>

          {/* 유형 */}
          <div style={S.field}>
            <label style={S.label}>유형</label>
            <select style={S.input} value={it.item_type}
              onChange={e => updateItem(idx, 'item_type', e.target.value)}>
              {ITEM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          {/* 제목 / 내용 / URL */}
          <div style={S.field}>
            <label style={S.label}>
              {it.item_type === 'S' ? '중간 제목 *' : it.item_type === 'V' ? '영상 URL *' : '질문 내용 *'}
            </label>
            <textarea style={{ ...S.input, minHeight: 60, resize: 'vertical', fontFamily: "'Noto Sans KR', sans-serif" }}
              value={it.item_title}
              onChange={e => updateItem(idx, 'item_title', e.target.value)}
              placeholder={
                it.item_type === 'S' ? '구분 제목을 입력하세요'
                : it.item_type === 'V' ? '영상URL'
                : '질문을 입력하세요'
              } />
            {it.item_type === 'V' && it.item_title && !getYoutubeEmbedUrl(it.item_title) && (
              <div style={{ fontSize: 12, color: '#ff4d4f', marginTop: 4 }}>⚠️ 올바른 유튜브 URL 형식이 아닙니다.</div>
            )}
            {it.item_type === 'V' && getYoutubeEmbedUrl(it.item_title) && (
              <div style={{ marginTop: 8, aspectRatio: '16/9', maxWidth: 480 }}>
                <iframe
                  src={getYoutubeEmbedUrl(it.item_title)}
                  style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={`영상 미리보기 ${idx + 1}`} />
              </div>
            )}
          </div>

          {/* 필수여부 - 질문 항목만 (구분/영상 제외) */}
          {!['S', 'V'].includes(it.item_type) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <input type="checkbox" id={`req-${idx}`} checked={it.required_yn === 'Y'}
                onChange={e => updateItem(idx, 'required_yn', e.target.checked ? 'Y' : 'N')} />
              <label htmlFor={`req-${idx}`} style={{ fontSize: 13, color: '#666', cursor: 'pointer' }}>필수 응답</label>
            </div>
          )}

          {/* 이미지 - 영상 항목 제외 */}
          {it.item_type !== 'V' && (
            <div style={S.field}>
              <label style={S.label}>이미지 (선택, 1MB 이하)</label>
              <input type="file" accept="image/*" onChange={e => handleImgUpload(idx, e)} />
              {it.img_preview && (
                <div style={{ marginTop: 8 }}>
                  <img src={it.img_preview} alt="preview"
                    style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, border: '1px solid #eee' }} />
                  <button style={{ ...S.btn, ...S.btnDanger, marginTop: 4, padding: '2px 10px', fontSize: 12 }}
                    onClick={() => { updateItem(idx, 'img_data', null); updateItem(idx, 'img_preview', null); }}>
                    이미지 삭제
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 객관식/체크박스 보기 - 기존 그대로 */}
          {['M', 'C'].includes(it.item_type) && (
            <div style={S.field}>
              <label style={S.label}>보기</label>
              {it.options.map((o, oi) => (
                <div key={oi} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <input style={{ ...S.input, flex: 1 }} value={o}
                    onChange={e => updateOption(idx, oi, e.target.value)}
                    placeholder={`보기 ${oi + 1}`} />
                  {it.options.length > 2 && (
                    <button style={{ ...S.btn, ...S.btnDanger, padding: '4px 10px' }}
                      onClick={() => removeOption(idx, oi)}>✕</button>
                  )}
                </div>
              ))}
              <button style={{ ...S.btn, background: '#f0f0f0', color: '#333', marginTop: 4 }}
                onClick={() => addOption(idx)}>+ 보기 추가</button>
            </div>
          )}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <button style={{ ...S.btn, background: '#f6ffed', color: '#52c41a', border: '1px dashed #52c41a', flex: 1 }}
          onClick={addItem}>+ 항목 추가</button>
        <button style={{ ...S.btn, background: '#f9f0ff', color: '#722ed1', border: '1px dashed #722ed1', flex: 1 }}
          onClick={() => setItems(prev => [...prev, { item_type: 'S', item_title: '', options: [], img_data: null, img_preview: null, required_yn: 'N' }])}>
          + 구분(중간제목) 추가
        </button>
      </div>

      {error && <div style={S.error}>{error}</div>}
      <button style={{ ...S.btn, ...S.btnPrimary, width: '100%', opacity: loading ? 0.7 : 1 }}
        onClick={handleSaveItems} disabled={loading}>
        {loading ? '저장 중...' : '항목 저장'}
      </button>
    </div>
  );

  if (view === 'result') return (
    <div style={S.wrap}>
      <div style={S.header}>
        <button style={S.backBtn} onClick={() => setView('list')}>← 목록</button>
        <h2 style={S.title}>결과 — {selected?.title}</h2>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>로딩 중...</div>
      ) : error ? (
        <div style={S.error}>{error}</div>
      ) : result && (
        <>
          <div style={{ ...S.card, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontSize: 24, fontWeight: 800, color: '#1677ff' }}>{result.answerCount}</span>
              <span style={{ fontSize: 14, color: '#888', marginLeft: 8 }}>명 제출</span>
            </div>
            <button style={{ ...S.btn, background: '#52c41a', color: '#fff' }} onClick={handleExcelDownload}>
              📥 엑셀 다운로드
            </button>
          </div>

          {result.items.map(it => (
            <div key={it.item_no} style={{ ...S.card, marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12, whiteSpace: 'pre-wrap' }}>
                {it.item_no}. {it.item_type === 'V' ? '영상' : it.item_title}
              </div>
              <SurveyResultChart item={it} />
            </div>
          ))}
        </>
      )}
    </div>
  );

  // ==================== 목록 (기본) ====================
  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <h2 style={S.title}>📝 설문조사</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select style={S.yearSelect} value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
            {getYearOptions().map(y => (
              <option key={y} value={y}>{y}년</option>
            ))}
          </select>
          <button style={{ ...S.btn, ...S.btnPrimary }} onClick={handleCreate}>+ 등록</button>
        </div>
      </div>

      {list.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>등록된 설문이 없습니다.</div>
      )}

      {list.map(item => (
        <div key={item.seq_no} style={{ ...S.card, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, marginRight: 8,
                background: item.target_type === 'U' ? '#e6f4ff' : '#f5f5f5',
                color: item.target_type === 'U' ? '#1677ff' : '#888',
              }}>
                {item.target_type === 'U' ? '조합원' : '전직원'}
              </span>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{item.title}</span>
              <span style={{
                marginLeft: 10, fontSize: 12, fontWeight: 600,
                color: STATUS_COLOR[item.status]
              }}>● {STATUS_LABEL[item.status]}</span>
            </div>
            <span style={{ fontSize: 12, color: '#aaa' }}>{item.reg_dt?.slice(0, 10)}</span>
          </div>
          <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>제출 {item.answer_cnt || 0}건</div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {/* 상태 버튼 */}
            {item.status === 'S' && (
              <button style={{ ...S.btn, background: '#52c41a', color: '#fff' }}
                onClick={() => handleStatus(item, 'P')}>▶ 시작</button>
            )}
            {item.status === 'P' && (
              <button style={{ ...S.btn, background: '#ff4d4f', color: '#fff' }}
                onClick={() => handleStatus(item, 'E')}>■ 종료</button>
            )}
            {item.status === 'E' && (
              <button style={{ ...S.btn, background: '#52c41a', color: '#fff' }}
                onClick={() => handleStatus(item, 'P')}>🔄 재시작</button>
            )}
            {(item.status === 'P' || item.status === 'E') && (
              <button style={{ ...S.btn, ...S.btnPrimary }}
                onClick={() => handleResult(item)}>📊 결과보기</button>
            )}
            {/* 편집 버튼 - 대기 상태에서만 (항목편집/제목수정) */}
            {item.status === 'S' && (
              <>
                <button style={{ ...S.btn, background: '#f0f0f0', color: '#333' }}
                  onClick={() => handleItems(item)}>항목편집</button>
                <button style={{ ...S.btn, background: '#f0f0f0', color: '#333' }}
                  onClick={() => handleEdit(item)}>제목수정</button>
              </>
            )}
            {/* 삭제 버튼 - 대기(시작전) 또는 종료(종료후) 상태에서 가능, 진행중일 때만 불가 */}
            {(item.status === 'S' || item.status === 'E') && (
              <button style={{ ...S.btn, ...S.btnDanger }}
                onClick={() => handleDelete(item)}>삭제</button>
            )}
            {/* 복사 - 모든 상태에서 가능 */}
            <button style={{ ...S.btn, background: '#f9f0ff', color: '#722ed1', border: '1px solid #d3adf7' }}
              onClick={() => handleCopy(item)}>📑 복사</button>
            {/* QR/링크 - 전직원(A)용일 때만 표시. 조합원(U)은 메인화면 메뉴로 접근하므로 불필요 */}
            {item.target_type !== 'U' && (
              <>
                <button style={{ ...S.btn, background: '#f0f0f0', color: '#333' }}
                  onClick={() => {
                    const url = buildUrl(item);
                    if (navigator.clipboard && window.isSecureContext) {
                      navigator.clipboard.writeText(url).then(() => alert('✅ 링크가 복사되었습니다!')).catch(() => fallbackCopy(url));
                    } else {
                      fallbackCopy(url);
                    }
                  }}>
                  📋 링크복사
                </button>
                <button style={{ ...S.btn, background: '#f0f0f0', color: '#333' }}
                  onClick={() => setQrTarget(qrTarget?.seq_no === item.seq_no ? null : item)}>
                  🔳 QR
                </button>
              </>
            )}
          </div>

          {item.target_type !== 'U' && qrTarget?.seq_no === item.seq_no && (
            <div style={{ marginTop: 12, textAlign: 'center', padding: '16px 0', borderTop: '1px solid #f0f0f0' }}>
              <QRCodeSVG value={buildUrl(item)} size={160} />
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 8, wordBreak: 'break-all' }}>{buildUrl(item)}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ==================== 결과 차트 컴포넌트 ====================

function SurveyResultChart({ item }) {
  // 단답/서술형 - 응답 목록 그대로 표시
  if (item.item_type === 'T1' || item.item_type === 'T2') {
    if (item.answers.length === 0) return <div style={{ color: '#aaa' }}>응답 없음</div>;
    return (
      <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {item.answers.map((a, i) => (
          <div key={i} style={{ background: '#fafafa', borderRadius: 8, padding: '8px 12px', fontSize: 14, color: '#333' }}>
            {a.answer_text}
          </div>
        ))}
      </div>
    );
  }

  // 객관식 - 단일선택 집계
  if (item.item_type === 'M') {
    const counts = {};
    item.answers.forEach(a => { counts[a.answer_text] = (counts[a.answer_text] || 0) + 1; });
    const total = item.answers.length;
    return (
      <div>
        {item.options.map(o => {
          const cnt = counts[o.opt_name] || 0;
          const pct = total > 0 ? Math.round(cnt / total * 100) : 0;
          return (
            <div key={o.opt_no} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 4 }}>
                <span>{o.opt_name}</span>
                <span style={{ color: '#888' }}>{cnt}명 ({pct}%)</span>
              </div>
              <div style={{ background: '#f0f0f0', borderRadius: 6, height: 20 }}>
                <div style={{ width: `${pct}%`, background: '#1677ff', height: '100%', borderRadius: 6, transition: 'width 0.5s' }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // 체크박스 - 다중선택 집계 (쉼표 분리)
  if (item.item_type === 'C') {
    const counts = {};
    item.answers.forEach(a => {
      (a.answer_text || '').split(',').filter(Boolean).forEach(v => {
        counts[v] = (counts[v] || 0) + 1;
      });
    });
    const totalRespondents = item.answers.length;
    return (
      <div>
        {item.options.map(o => {
          const cnt = counts[o.opt_name] || 0;
          const pct = totalRespondents > 0 ? Math.round(cnt / totalRespondents * 100) : 0;
          return (
            <div key={o.opt_no} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 4 }}>
                <span>{o.opt_name}</span>
                <span style={{ color: '#888' }}>{cnt}명 ({pct}%)</span>
              </div>
              <div style={{ background: '#f0f0f0', borderRadius: 6, height: 20 }}>
                <div style={{ width: `${pct}%`, background: '#52c41a', height: '100%', borderRadius: 6, transition: 'width 0.5s' }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (item.item_type === 'V') {
    const embedUrl = getYoutubeEmbedUrl(item.item_title);
    if (!embedUrl) return <div style={{ color: '#aaa' }}>영상 URL 오류</div>;
    return (
      <div style={{ aspectRatio: '16/9', maxWidth: 480 }}>
        <iframe src={embedUrl} style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen title="영상" />
      </div>
    );
  }

  return null;
}

// ==================== 스타일 ====================

const S = {
  wrap      : { maxWidth: 1200, margin: '0 auto', padding: '24px 16px', width: '100%', boxSizing: 'border-box' },
  main      : { maxWidth: 1200, margin: "0 auto", width: "100%" },
  header    : { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title     : { fontSize: 20, fontWeight: 800, color: '#1a1a2e', margin: 0 },
  backBtn   : { background: 'none', border: 'none', fontSize: 14, color: '#1677ff', cursor: 'pointer', padding: 0 },
  card      : { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', width: '100%', boxSizing: 'border-box' },
  field     : { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14, width: '100%' },
  label     : { fontSize: 13, fontWeight: 600, color: '#444' },
  input     : { border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', background: '#fafafa', width: '100%', boxSizing: 'border-box' },
  btn       : { border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnPrimary: { background: '#1677ff', color: '#fff' },
  btnDanger : { background: '#fff2f0', color: '#ff4d4f', border: '1px solid #ffccc7' },
  error     : { background: '#fff5f5', border: '1px solid #ffcccc', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#c0392b', marginBottom: 12 },
  yearSelect: { border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none', background: '#fff', cursor: 'pointer' },  // ← 추가
};